import { FlashList, ListRenderItemInfo } from '@shopify/flash-list';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, RefreshControl, StyleSheet } from 'react-native';

import { Box, Text } from '@components//Restyle';
import { ActivityItem } from '@components/ActivityList/ActivityItem';
import {
  ActivityRow,
  buildActivityRows,
  formatActivityDateHeader,
} from '@components/ActivityList/activityListHelpers';
import ProjectActivityItem, {
  getProjectActivityMessage,
  ProjectActivityType,
} from '@components/ActivityList/ProjectActivityItems';
import TaskActivityItem, {
  getTaskActivityMessage,
  TaskActivityType,
} from '@components/ActivityList/TaskActivityItems';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { PaddingBottomListFooter } from '@components/shared/ListSpacers';
import {
  ChatDrawerScreen,
  useNavigateToChatDrawer,
} from '@components/Web/Drawer/WebDrawerContext';
import {
  Audit,
  SearchableResult,
  User,
  useSearchLazyQuery,
} from '@graphql/generated';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import useGlobalSearch from '@hooks/useGlobalSearch';
import useMe from '@hooks/useMe';
import useSearch from '@hooks/useSearch';
import { Search } from '@root/types';
import theme from '@themes/theme';

// Re-export helpers so existing consumers importing from this module still work
export { buildActivityRows, formatActivityDateHeader };
export type { ActivityRow };

export const capitalizeFirstLetter = (str: string) => {
  if (str) return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  return '';
};

/**
 * Resolve the badge event key for an audit.
 * For Checkin audits that carry a check-in attachment, map to the
 * synthetic keys 'CheckinSuccess' or 'CheckinMismatch' so the badge
 * component can render the correct MapPin colour.
 */
const resolveBadgeEvent = (audit: Audit): string => {
  if (audit.event === ProjectActivityType.Checkin) {
    const checkin = (audit.attachment as any)?.checkin;
    if (checkin) {
      return checkin.withinGeofence === false
        ? 'CheckinMismatch'
        : 'CheckinSuccess';
    }
  }
  if (audit.event === 'MessageSent') {
    const hasDocument = (audit.attachment as any)?.attachments?.some(
      (a: any) => a.__typename === 'Document'
    );
    if (hasDocument) return 'MessageSentWithDocument';
  }
  return audit.event;
};

/**
 * `ListEmptyComponent` (typescript:S6478, TA-10796 QA follow-up). SonarCloud
 * never scans `.web.tsx` files (confirmed empty query results for this rule
 * across every `.web.tsx` in this repo), so this file's own inline render
 * props were never caught by the live-Sonar-scoped extraction the sibling
 * `ActivityList.tsx` went through — QA found this one by inspection instead.
 * A stable module-scope component passed as an *element* (not a function)
 * means FlashList's `getValidComponent` (`isValidElement(X) ? X :
 * createElement(X)`) uses it directly — the type never changes, so only
 * props reconcile instead of remounting.
 */
const ActivityListEmptyState: React.FC<{ visible: boolean }> = ({
  visible,
}) => {
  const { t } = useTranslation();
  if (!visible) return null;
  return (
    <Box alignItems='center'>
      <Text>{t('shared:NoRecordFound')}</Text>
    </Box>
  );
};

/**
 * Wraps a caller-supplied `listHeaderComponent`/`listFooterComponent`
 * render-prop so it's never used as `ListHeaderComponent`/
 * `ListFooterComponent`'s component *type* directly (PR #6058 review, rule
 * 12). Callers across the app (GlobalActivityLog, TaskActivityLog,
 * ProjectActivityLog, TasksTabList) commonly build this function fresh on
 * every render — e.g. `GlobalActivityLog.web.tsx:915-929`'s `isPaywalled ?
 * () => (...) : () => (...)` — which would otherwise remount the
 * header/footer on every single parent rerender. This wrapper's own type
 * never changes, only its `render` prop does, so FlashList's
 * `getValidComponent` reconciles it in place instead.
 */
const SuppliedListSlot: React.FC<{ render: () => React.JSX.Element }> = ({
  render,
}) => render();

type ActivityListProps = {
  audits: Audit[];
  activityType: 'Task' | 'Project' | 'Global';
  filterValForGlobalActivity?: string;
  projectId?: string;
  taskId?: string;
  onRefresh?: () => void;
  /** Controls loading indicator when fetching additional projects */
  refreshing?: boolean;
  fetchMore?: () => void;
  loadingMore?: boolean;
  listFooterComponent?: () => React.JSX.Element;
  renderEmptyActiveComponent?: () => React.JSX.Element;
  showFooterWhenEmpty?: boolean;
  onEndReached?: () => void;
  listHeaderComponent?: () => React.JSX.Element;
  externalNavigate?: any;
  noMargin?: boolean;
};

const ActivityList: React.FC<ActivityListProps> = ({
  audits,
  activityType,
  projectId,
  taskId,
  onRefresh,
  refreshing = false,
  listFooterComponent,
  onEndReached,
  renderEmptyActiveComponent,
  showFooterWhenEmpty = false,
  listHeaderComponent = () => <Box marginTop='l' />,
  filterValForGlobalActivity,
  externalNavigate,
}) => {
  const { me } = useMe();
  const { setIsSearching } = useGlobalSearch();
  const navigation = useNavigateToChatDrawer();

  const filteredAudits = useMemo(
    () =>
      audits
        ?.filter((a) => {
          const taskAssignments =
            a?.attachment?.__typename === 'Message'
              ? a.attachment.attachments?.filter(
                  (item) => item.__typename === 'MessageTaskAssignment'
                )
              : undefined;
          return (
            !(taskAssignments && taskAssignments.length > 0) &&
            !(
              a?.attachment?.__typename === 'Message' &&
              a.attachment.userRequest
            ) &&
            getProjectActivityMessage(a.event as ProjectActivityType) !== ''
          );
        })
        .sort((a, b) => (+b.id > +a.id ? 1 : -1)),
    [audits]
  );

  // Interleaved rows with sticky date headers
  const rows = useMemo(
    () => buildActivityRows(filteredAudits),
    [filteredAudits]
  );

  const { search } = useSearch();

  const [globalSearch, { data, loading: loadingSearch }] = useSearchLazyQuery();

  const fileResults = (data?.search as Search<SearchableResult>[]) || [];
  const handleGlobalSearch = (term: string) => {
    // The web render path below doesn't use fileResults/search filtering at
    // all (it just renders `rows` unfiltered) — skip firing the query so we
    // don't waste a network call whose result is never displayed.
    if (Platform.OS === 'web') return;
    globalSearch({
      variables: {
        term,
        size: 20,
        projectId: projectId ? projectId : null,
        taskId: taskId ? taskId : null,
        includeMessages: false,
        includeDocuments: false,
        includeProjects: false,
        includeTasks: false,
        includeContacts: false,
        includeChats: false,
        includeActivities: true,
      },
    });
  };
  const { isPending, isBelowThreshold, hasSearched } = useDebouncedSearch(
    search,
    handleGlobalSearch
  );
  // Below the 3-char threshold, treat this the same as no active search —
  // fall back to the audits passed in via props (TA-10170).
  const isSearchActive = !isBelowThreshold;
  // Safe form: gate on !isBelowThreshold first so a request still resolving
  // right as the term drops below threshold can't leak a stale spinner
  // through (see useDebouncedSearch's isPending/hasSearched docs).
  const isSearchLoading =
    isSearchActive && (loadingSearch || isPending || !hasSearched);

  const getMessage = (item: Audit) => {
    const { attachment, user } = item;

    if (attachment?.__typename === 'Message') {
      if (
        (attachment.attachments ?? []).filter(
          (a) =>
            a.__typename !== 'Document' &&
            a.__typename !== 'MessageProject' &&
            a.__typename !== 'MessageTask'
        ).length === 0 ||
        attachment.checkin
      )
        return ['', null];
    }
    let assignedUser: User | null | undefined =
      attachment != null && 'user' in attachment ? attachment.user : null;

    const isProjectActivityType = (
      value: any
    ): value is ProjectActivityType => {
      return Object.values(ProjectActivityType).includes(value);
    };
    const isTaskActivityType = (value: any): value is TaskActivityType => {
      return Object.values(TaskActivityType).includes(value);
    };
    let userName = '';
    if (
      item.event === TaskActivityType.TaskAssigned ||
      item.event === TaskActivityType.TaskUnassigned ||
      item.event === ProjectActivityType.TaskAssigned ||
      item.event === ProjectActivityType.TaskUnassigned ||
      item.event === ProjectActivityType.ProjectAssigned ||
      item.event === ProjectActivityType.ProjectUnassigned ||
      item.event === ProjectActivityType.TaskReassigned ||
      item.event === TaskActivityType.TaskReassigned
    ) {
      userName = '@' + (assignedUser?.name ?? 'someone');
    }

    if (item.event === ProjectActivityType.TaskDeleted) {
      userName = '@' + (user?.name ?? 'someone');
      assignedUser = user;
    }
    if (
      activityType === 'Project' ||
      (activityType === 'Global' && isProjectActivityType(item.event))
    ) {
      return [
        getProjectActivityMessage(
          item.event as ProjectActivityType,
          item.projectRole != null
            ? capitalizeFirstLetter(item.projectRole)
            : undefined,
          attachment?.__typename === 'Message'
            ? (attachment.body ?? undefined)
            : undefined,
          item?.changes,
          item?.attachment,
          userName
        ) || '',
        assignedUser,
      ];
    } else if (
      activityType === 'Task' ||
      (activityType === 'Global' && isTaskActivityType(item.event))
    ) {
      return [
        getTaskActivityMessage(
          item.event as TaskActivityType,
          attachment?.__typename === 'Message'
            ? (attachment.body ?? undefined)
            : undefined,
          item?.changes,
          userName
        ) || '',
        assignedUser,
      ];
    } else {
      return ['', null];
    }
  };

  const getChatId = (item: Audit) => {
    if (item?.attachment?.__typename === 'Message') {
      return item.attachment.chatId;
    }
  };

  const renderAuditItem = (
    audit: Audit,
    index: number,
    isLastInGroup: boolean
  ) => {
    const chatId = getChatId(audit);
    const userId = audit?.user?.id;
    const messageAttachment =
      audit?.attachment?.__typename === 'Message'
        ? audit.attachment
        : undefined;
    // .some() returns a boolean — use it directly, not .length
    const userExistInChat =
      messageAttachment?.chat?.users?.some((u) => u?.id === me?.id) ?? false;

    let needBoldString: string[] = [];
    if (audit.event === ProjectActivityType.ProjectUpdated) {
      try {
        // Name-type changes bold both the previous and the new name
        // (e.g. "Project name has been updated from Old to New."). Other
        // change types (location, team) only bold the new value, matching
        // their "...has been changed to X." wording.
        needBoldString =
          audit?.changes?.[0]?.project?.flatMap((change: any) => {
            if (change?.type === 'name') {
              return [change?.from, change?.to]
                .filter((v): v is string => Boolean(v))
                .map((v) => ' ' + v);
            }
            return change?.to ? [' ' + change.to] : [];
          }) || [];
      } catch {
        needBoldString = [];
      }
    } else if (
      audit.event === ProjectActivityType.ProjectReassigned ||
      audit.event === ProjectActivityType.TaskReassigned
    ) {
      try {
        needBoldString = [
          ' ' + capitalizeFirstLetter(audit?.changes?.[0]?.roles?.from),
          ' ' + capitalizeFirstLetter(audit?.changes?.[0]?.roles?.to),
        ];
      } catch {
        needBoldString = [];
      }
    }

    const [msg, linkUser] = getMessage(audit) as [
      string,
      User | null | undefined,
    ];
    const isGlobal = activityType === 'Global';
    const isWeb = Platform.OS === 'web';
    return (
      <ActivityItem
        externalNavigate={externalNavigate}
        user={audit.user}
        needBoldString={needBoldString}
        message={msg}
        createdAt={audit.createdAt}
        filterVal={
          filterValForGlobalActivity ?? (isGlobal ? undefined : search)
        }
        linkUserInMessage={linkUser ?? undefined}
        // Bottom separator between items is a web-only visual treatment
        showBottomSeparator={isWeb ? !isLastInGroup : true}
        // event prop drives the badge icon; web-only, undefined on native
        event={isWeb ? resolveBadgeEvent(audit) : undefined}
        onChatPress={
          // userExistInChat is a boolean — use it directly
          chatId && userId !== me?.id && userExistInChat
            ? () => {
                setIsSearching(true);
                navigation({
                  screen: ChatDrawerScreen.details,
                  activeChat: messageAttachment?.chat,
                  activeMessageId: messageAttachment?.id,
                });
              }
            : undefined
        }
        index={filteredAudits.length - index}
        audit={audit}
        activityType={activityType}
        // isProject={!!projectId}
      >
        {activityType === 'Project' || isGlobal ? (
          <ProjectActivityItem
            externalNavigate={externalNavigate}
            audit={audit}
            doNotUseGlobalSearch={isWeb || isGlobal}
            filterVal={
              filterValForGlobalActivity ?? (isGlobal ? undefined : '')
            }
            // activityMode drives web-only UI changes (borderless tags, truncation)
            activityMode={isWeb && isGlobal}
            isProject={!!projectId}
          />
        ) : (
          <TaskActivityItem
            doNotUseGlobalSearch={isWeb}
            externalNavigate={externalNavigate}
            audit={audit}
            taskId={taskId}
          />
        )}
      </ActivityItem>
    );
  };

  // Render a single row (either a sticky date header or an activity item)
  const renderRow = ({ item }: ListRenderItemInfo<ActivityRow>) => {
    if (item.type === 'header') {
      return (
        <Box style={styles.dateHeader}>
          <Text variant='webSmall'>{item.label}</Text>
        </Box>
      );
    }
    return renderAuditItem(item.audit, item.indexInGroup, item.isLastInGroup);
  };

  const keyExtractor = (item: ActivityRow) =>
    item.type === 'header' ? `header-${item.dateKey}` : item.audit.id;

  if (Platform.OS === 'web') {
    if (filteredAudits.length === 0 && renderEmptyActiveComponent) {
      if (showFooterWhenEmpty && listFooterComponent) {
        return (
          <Box flex={1}>
            {renderEmptyActiveComponent()}
            {listFooterComponent()}
          </Box>
        );
      }
      return (
        <Box flex={1} alignSelf='center' justifyContent='center'>
          {renderEmptyActiveComponent()}
        </Box>
      );
    }

    return (
      <Box flex={1}>
        <FlashList
          contentContainerStyle={{
            paddingHorizontal: theme.spacing.none,
          }}
          data={rows}
          renderItem={renderRow}
          onEndReached={onEndReached}
          onEndReachedThreshold={0.5}
          keyExtractor={keyExtractor}
          estimatedItemSize={56}
          getItemType={(item) => item.type}
          ListHeaderComponent={<Box />}
          ListFooterComponent={
            listFooterComponent ? (
              <SuppliedListSlot render={listFooterComponent} />
            ) : (
              <PaddingBottomListFooter />
            )
          }
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          showsVerticalScrollIndicator={Platform.OS === 'web'}
        />
      </Box>
    );
  }

  // ── Native (iOS/Android) path ────────────────────────────────────
  const activityDataList =
    isSearchActive && hasSearched ? fileResults.map((r) => r.record) : [];
  if (isSearchLoading) return <ActivityIndicatorLoading />;

  const nativeRows = isSearchActive
    ? buildActivityRows(activityDataList as Audit[])
    : rows;

  return (
    <Box flex={1}>
      {filteredAudits.length === 0 &&
      !isSearchActive &&
      renderEmptyActiveComponent ? (
        <Box flex={1} alignSelf='center' justifyContent='center'>
          {renderEmptyActiveComponent()}
        </Box>
      ) : (
        <FlashList
          contentContainerStyle={{
            paddingHorizontal: theme.spacing.m,
          }}
          data={nativeRows}
          renderItem={renderRow}
          onEndReached={onEndReached}
          onEndReachedThreshold={0.5}
          keyExtractor={keyExtractor}
          estimatedItemSize={56}
          getItemType={(item) => item.type}
          ListHeaderComponent={
            <SuppliedListSlot render={listHeaderComponent} />
          }
          ListFooterComponent={
            !isSearchActive && listFooterComponent ? (
              <SuppliedListSlot render={listFooterComponent} />
            ) : (
              <PaddingBottomListFooter />
            )
          }
          ListEmptyComponent={
            <ActivityListEmptyState
              visible={isSearchActive && !isSearchLoading}
            />
          }
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          showsVerticalScrollIndicator={false}
        />
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  // Sticky date section header — grey01 bg, padding matches Figma spec:
  // pt:8 pr:16 pb:8 pl:16
  dateHeader: {
    backgroundColor: theme.colors.grey01,
    paddingTop: theme.spacing.xs, // 8px
    paddingBottom: theme.spacing.xs, // 8px
    paddingHorizontal: theme.spacing.m, // 16px
    marginBottom: theme.spacing.m, // 16px
  },
});

export default ActivityList;
